import React, { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  Modal,
  Tile,
  InlineNotification,
  Loading,
  InlineLoading,
} from '@carbon/react';
import { TrashCan, CheckmarkFilled } from '@carbon/react/icons';
import { useNavigate } from 'react-router-dom';

const API_ROOT = '/mlapi/premium/integration/openclaw/computers';

const OpenClawIntegrationPage = ({ token }) => {
  const nav = useNavigate();

  const [computers, setComputers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slackBotToken, setSlackBotToken] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [openclawBotName, setOpenclawBotName] = useState('OpenClaw');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [banner, setBanner] = useState(null);

  const hdr = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    fetch(API_ROOT, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setComputers)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const openModal = () => {
    setName('');
    setSlackBotToken('');
    setSlackChannelId('');
    setOpenclawBotName('OpenClaw');
    setErr('');
    setOpen(true);
  };

  const save = () => {
    if (!name) {
      setErr('Display name is required');
      return;
    }
    if (!slackBotToken) {
      setErr('Slack bot token is required');
      return;
    }
    if (!slackChannelId) {
      setErr('Slack channel ID is required');
      return;
    }

    setSaving(true);
    setErr('');
    fetch(API_ROOT, {
      method: 'POST',
      headers: hdr,
      body: JSON.stringify({
        name,
        slack_bot_token: slackBotToken,
        slack_channel_id: slackChannelId,
        openclaw_bot_name: openclawBotName || 'OpenClaw',
        computer_type: 'openclaw',
      }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.detail || 'Save failed');
        setComputers(prev => [...prev, data]);
        setBanner({ name: data.name });
        setOpen(false);
      })
      .catch(e => setErr(e.message))
      .finally(() => setSaving(false));
  };

  const del = id => {
    if (!window.confirm('Remove this computer from FFAI?')) return;
    fetch(`${API_ROOT}/${id}`, { method: 'DELETE', headers: hdr })
      .then(r => r.ok && setComputers(computers.filter(c => c.id !== id)))
      .catch(() => alert('Delete failed'));
  };

  const testConnection = computer => {
    setTesting(computer.id);
    fetch(`${API_ROOT}/${computer.id}/test`, { method: 'POST', headers: hdr })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok) alert(`✓ ${computer.name}: ${data.message}`);
        else alert(`✗ ${computer.name}: ${data.detail}`);
      })
      .catch(() => alert('Test request failed'))
      .finally(() => setTesting(null));
  };

  return (
    <div className="openclaw-integration-page">
      <div className="openclaw-integration-container">
        <h1>OpenClaw / NemoClaw Computers</h1>
        <p>
          Register OpenClaw or NemoClaw computers so Fresh&nbsp;Focus&nbsp;AI
          can delegate tasks to the AI agents running on those machines —
          relayed via Slack. The computer just needs to be online with OpenClaw
          running; no port forwarding required.
        </p>

        <div
          style={{
            background: '#f4f4f4',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '1rem',
            marginBottom: '1.5rem',
          }}>
          <strong>Setup (one time per computer):</strong>
          <ol
            style={{
              marginTop: '0.5rem',
              marginBottom: 0,
              paddingLeft: '1.25rem',
            }}>
            <li>
              In your Slack workspace, create a channel (e.g.{' '}
              <code>#ffai-tasks</code>) and invite the OpenClaw bot
            </li>
            <li>
              Create a Slack bot app (or use an existing one) with{' '}
              <code>chat:write</code> and <code>channels:history</code> scopes,
              and copy its Bot Token
            </li>
            <li>
              Find the channel ID: right-click the channel in Slack → Copy link
              → the ID is the last part (e.g. <code>C08ABC123</code>)
            </li>
            <li>
              Register below — FFAI will send tasks to that channel and read
              OpenClaw's response
            </li>
          </ol>
        </div>

        {banner && (
          <InlineNotification
            lowContrast
            kind="success"
            title={
              <span>
                <CheckmarkFilled style={{ marginRight: '0.5rem' }} />
                {`"${banner.name}" registered`}
              </span>
            }
            subtitle="Tasks sent to this computer will be relayed via Slack to your OpenClaw agent."
            onClose={() => setBanner(null)}
            hideCloseButton={false}
            style={{ marginBottom: '1rem' }}
          />
        )}

        <Button kind="primary" onClick={openModal}>
          Add computer
        </Button>

        <div style={{ marginTop: '1.5rem' }}>
          {loading ? (
            <Loading active withOverlay={false} />
          ) : computers.length ? (
            computers.map(c => (
              <Tile key={c.id} style={{ marginBottom: '1rem' }}>
                <h3 style={{ marginBottom: '0.25rem' }}>{c.name}</h3>
                <p
                  className="bx--type-caption"
                  style={{ marginBottom: '0.1rem' }}>
                  Channel: {c.slack_channel_id} &mdash; OpenClaw bot:{' '}
                  {c.openclaw_bot_name}
                </p>
                <p className="bx--type-caption" style={{ color: '#6f6f6f' }}>
                  Added {new Date(c.created_at).toLocaleDateString()}
                </p>
                <div
                  style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                  {testing === c.id ? (
                    <InlineLoading description="Testing…" />
                  ) : (
                    <Button
                      kind="tertiary"
                      size="sm"
                      onClick={() => testConnection(c)}>
                      Test connection
                    </Button>
                  )}
                  <Button
                    kind="danger--tertiary"
                    size="sm"
                    renderIcon={TrashCan}
                    onClick={() => del(c.id)}>
                    Remove
                  </Button>
                </div>
              </Tile>
            ))
          ) : (
            <p style={{ color: '#6f6f6f' }}>No computers registered yet.</p>
          )}
        </div>

        {computers.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h2>Example prompts</h2>
            <ul>
              <li>
                <strong>Delegate a task:</strong> "On my home computer, check
                what Docker containers are running"
              </li>
              <li>
                <strong>Run a command:</strong> "Ask my base2u openclaw to
                summarize the latest system logs"
              </li>
              <li>
                <strong>Multi-step:</strong> "Use my home computer to pull the
                latest git changes and run the tests"
              </li>
            </ul>
          </div>
        )}

        <Modal
          open={open}
          modalHeading="Register an OpenClaw computer"
          primaryButtonText={saving ? 'Connecting…' : 'Save & verify'}
          primaryButtonDisabled={saving}
          secondaryButtonText="Cancel"
          onRequestClose={() => setOpen(false)}
          onRequestSubmit={save}
          size="md">
          <TextInput
            id="oc-name"
            labelText="Display name"
            placeholder="e.g. Home computer, Office workstation"
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />
          <TextInput
            id="oc-token"
            labelText="Slack bot token"
            type="password"
            placeholder="xoxb-..."
            helperText="Bot token from your Slack app — needs chat:write and channels:history scopes"
            value={slackBotToken}
            onChange={e => setSlackBotToken(e.target.value)}
            autoComplete="off"
            style={{ marginBottom: '1rem' }}
          />
          <TextInput
            id="oc-channel"
            labelText="Slack channel ID"
            placeholder="C08ABC123"
            helperText="The ID of the channel where your OpenClaw bot is invited (not the name)"
            value={slackChannelId}
            onChange={e => setSlackChannelId(e.target.value)}
            style={{ marginBottom: '1rem' }}
          />
          <TextInput
            id="oc-botname"
            labelText="OpenClaw bot display name in Slack"
            placeholder="OpenClaw"
            helperText="The name OpenClaw appears as in your Slack channel — used to identify its responses"
            value={openclawBotName}
            onChange={e => setOpenclawBotName(e.target.value)}
          />
          {err && (
            <p
              style={{
                color: '#da1e28',
                marginTop: '1rem',
                fontSize: '0.875rem',
              }}>
              {err}
            </p>
          )}
        </Modal>

        <div style={{ marginTop: '2rem' }}>
          <Button kind="secondary" onClick={() => nav('/')}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OpenClawIntegrationPage;
